import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { Shield, Calendar, Home, FileText } from "lucide-react";
import { useSEO, useSEOSettings, generateBreadcrumbSchema, generateFAQSchema, generateKnowledgeGraphSchema, generateSpeakableSchema, type SiteInfoSettings } from "@/lib/seo-utils";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { PageHero } from "@/components/layout/page-hero";
import { FaqSection } from "@/components/seo/faq-section";
import { SectionHeading } from "@/components/ui/section-heading";
import { formatTimeToStandard } from "@/lib/time-utils";
import { ContactActionLinks } from "@/components/contact/contact-action-links";
import { wavesVideo, parallaxBg, wavesPoster } from "@/lib/media-assets";
import type { PublicProperty } from "@shared/schema";
import { BOOK_DIRECT_SAVINGS, STRIPE_SECURE_PAYMENT, SECURITY_DEPOSIT_SUMMARY } from "@shared/property-facts";
import { HouseRulesDisplay } from "@/components/legal/house-rules-display";
import { useSiteBrand } from "@/hooks/use-site-brand";

export default function PoliciesPage() {
  const siteBrand = useSiteBrand();
  const { data: property } = useQuery<PublicProperty>({
    queryKey: ["/api/property/public"],
  });

  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ['/api/settings/site_info'],
  });

  const policyFaqs = [
    {
      question: "What is your cancellation policy?",
      answer: "We follow Airbnb's Firm cancellation policy: a full refund at least 30 days before check-in, half the nightly total between 7 and 30 days, and no nightly refund within 7 days. The cleaning fee is always returned. Contact us before booking if you need more flexibility.",
    },
    {
      question: "What time is check-in and check-out?",
      answer: `Standard check-in is ${property?.check_in_time ? formatTimeToStandard(property.check_in_time) : "3:00 PM"} and check-out is ${property?.check_out_time ? formatTimeToStandard(property.check_out_time) : "10:00 AM"} HST.`,
    },
    {
      question: "Are pets allowed?",
      answer: "Pets may be allowed with prior written approval and an additional cleaning fee. Service animals are welcome in accordance with applicable law.",
    },
    {
      question: "How do I book directly?",
      answer: `Reserve at vacationrentaloahu.co/booking. ${BOOK_DIRECT_SAVINGS} ${STRIPE_SECURE_PAYMENT}`,
    },
    {
      question: "Is there a security deposit?",
      answer: SECURITY_DEPOSIT_SUMMARY,
    },
  ];

  const { seoSettings } = useSEOSettings();

  const seoHead = useSEO({
    pageType: "policies",
    description: "Cancellation policy, house rules, check-in procedures, and rental terms for our Ka'a'awa beachfront vacation rental on Oahu.",
    keywords: "vacation rental policies Oahu, booking terms Hawaii, house rules Beach House rental, cancellation policy, rental agreement terms",
    twitterCard: "summary",
    canonical: "https://vacationrentaloahu.co/policies",
    structuredData: [
      generateKnowledgeGraphSchema({ seoSettings, siteInfoSettings: siteSettings }),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Policies & Terms", url: "/policies" }
      ]),
      generateFAQSchema(policyFaqs),
      generateSpeakableSchema(["h1", ".faq-answer"]),
    ]
  });

  return (
    <>
    {seoHead}
    <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
      <Header />
      
      <main id="main-content">
        <PageHero
          eyebrow="Guest Info"
          title="Policies & Terms"
          subtitle="Important information about booking policies, terms of service, and house rules for your stay at our Beach House Oahu property."
          videoSrc={wavesVideo}
          posterSrc={wavesPoster}
          imageAlt="Calm Oahu shoreline in Ka'a'awa"
        />

        <section className="py-16 md:py-24 bg-shell">
          <div className="container mx-auto px-6 max-w-4xl">
            <Breadcrumbs items={[{ name: "Home", url: "/" }, { name: "Policies", url: "/policies" }]} className="mb-16 justify-center" />

            <div className="bg-sand border border-sand-deep p-6 md:p-8 mb-10">
              <p className="text-ink leading-relaxed font-sans">
                {STRIPE_SECURE_PAYMENT} {BOOK_DIRECT_SAVINGS} The cancellation, house rules, and payment terms below apply to every direct reservation {siteBrand.portfolio ? "at our Ka'a'awa, Oahu homes" : "at The Kualoa Beach House in Ka'a'awa, Oahu"}.
              </p>
            </div>

            <div className="space-y-10 md:space-y-12">

              {/* Cancellation Policy */}
              <div id="cancellation" className="bg-sand border border-sand-deep p-8 md:p-12">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-shell flex items-center justify-center border border-sand-deep">
                    <Calendar className="h-6 w-6 text-ink" />
                  </div>
                  <h2 className="text-luxury-2xl font-serif text-forest tracking-luxury-tight">Cancellation Policy</h2>
                </div>
                
                <div className="space-y-8">
                  <div>
                    <h3 className="text-luxury-lg font-serif text-forest mb-3">Firm Cancellation Policy</h3>
                    <p className="text-ink-soft leading-relaxed">
                      This property follows Airbnb's Firm cancellation policy, the same terms that apply to
                      our Airbnb listing. Cancel at least 30 days before check-in and the stay is refunded
                      in full. The cleaning fee is always returned, whenever you cancel, because the clean
                      never takes place.
                    </p>
                  </div>

                  <div className="h-px bg-sand-deep w-full"></div>

                  <div>
                    <h3 className="text-luxury-lg font-serif text-forest mb-3">Refund Schedule</h3>
                    <ul className="space-y-3 text-ink-soft leading-relaxed">
                      <li className="flex gap-3"><span className="text-driftwood">-</span> <strong>Full Refund (30+ days):</strong> Cancel at least 30 days before check-in and the nightly total is refunded in full.</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> <strong>50% Refund (7-30 days):</strong> Cancel between 7 and 30 days before check-in and half the nightly total is refunded.</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> <strong>No Nightly Refund (&lt;7 days):</strong> Cancel within 7 days of check-in and the nightly total is not refunded.</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> <strong>Cleaning Fee:</strong> Always refunded in full, whenever you cancel.</li>
                    </ul>
                  </div>

                  <div className="h-px bg-sand-deep w-full"></div>

                  <div>
                    <h3 className="text-luxury-lg font-serif text-forest mb-3">Grace Periods</h3>
                    <p className="text-ink-soft leading-relaxed">
                      A full refund also applies if you cancel within 48 hours of booking and check-in is
                      still at least 14 days away, or within 24 hours of a booking made at least 7 days
                      before check-in.
                    </p>
                  </div>

                  <div className="border-l-2 border-sun bg-shell p-6 rounded-r-xl">
                    <p className="text-sm font-sans text-ink">
                      <strong className="uppercase tracking-widest text-[11px] text-driftwood block mb-1">Note</strong>
                      Cancellation policies may vary during peak seasons and holidays. Always review the specific policy for your dates during booking.
                    </p>
                  </div>
                </div>
              </div>

              {/* House Rules */}
              <div id="house-rules" className="bg-sand border border-sand-deep p-8 md:p-12">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-shell flex items-center justify-center border border-sand-deep">
                    <Home className="h-6 w-6 text-ink" />
                  </div>
                  <h2 className="text-luxury-2xl font-serif text-forest tracking-luxury-tight">House Rules</h2>
                </div>
                
                <div className="space-y-8">
                  <div className="grid md:grid-cols-3 gap-8">
                    <div>
                      <h3 className="text-luxury-base font-serif text-forest mb-3">Check-in / Out</h3>
                      <p className="text-ink-soft text-sm">Check-in: {formatTimeToStandard(property?.check_in_time) || '3:00 PM'} or later</p>
                      <p className="text-ink-soft text-sm mt-1">Check-out: {formatTimeToStandard(property?.check_out_time) || '10:00 AM'} or earlier</p>
                    </div>
                    
                    <div>
                      <h3 className="text-luxury-base font-serif text-forest mb-3">Occupancy</h3>
                      <p className="text-ink-soft text-sm">8 guests maximum</p>
                      <p className="text-ink-soft text-sm mt-1">No additional visitors without permission</p>
                    </div>
                    
                    <div>
                      <h3 className="text-luxury-base font-serif text-forest mb-3">No Smoking</h3>
                      <p className="text-ink-soft text-sm">Smoking strictly prohibited</p>
                      <p className="text-ink-soft text-sm mt-1">Anywhere on the property</p>
                    </div>
                  </div>

                  <div className="h-px bg-sand-deep w-full"></div>

                  {/*
                    Rendered from the published House Rules version rather than
                    listed here. A second hand-maintained copy is how this page
                    ended up advertising pets and 10:00 PM quiet hours while the
                    listing and the emails said otherwise.
                  */}
                  <div>
                    <h3 className="text-luxury-lg font-serif text-forest mb-4">Full house rules</h3>
                    <HouseRulesDisplay variant="summary" />
                  </div>

                  <div className="bg-sand p-6 rounded-xl border border-sand-deep">
                    <h4 className="font-serif text-forest mb-3">Beach Safety</h4>
                    <ul className="text-sm text-ink-soft space-y-2">
                      <li className="flex gap-2"><span className="text-driftwood">-</span> Children must be supervised at all times near the water</li>
                      <li className="flex gap-2"><span className="text-driftwood">-</span> Check ocean conditions and local warnings before entering the water</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Terms of Service */}
              <div id="terms" className="bg-sand border border-sand-deep p-8 md:p-12">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-shell flex items-center justify-center border border-sand-deep">
                    <FileText className="h-6 w-6 text-ink" />
                  </div>
                  <h2 className="text-luxury-2xl font-serif text-forest tracking-luxury-tight">Terms of Service</h2>
                </div>
                
                <div className="space-y-8">
                  <div>
                    <h3 className="text-luxury-lg font-serif text-forest mb-3">Booking Agreement</h3>
                    <p className="text-ink-soft leading-relaxed">
                      By making a reservation, you agree to these terms and conditions. The guest who makes 
                      the booking is responsible for all guests in the party and for ensuring all rules are followed.
                    </p>
                  </div>

                  <div className="h-px bg-sand-deep w-full"></div>

                  <div>
                    <h3 className="text-luxury-lg font-serif text-forest mb-3">Payment Terms</h3>
                    <ul className="space-y-3 text-ink-soft leading-relaxed">
                      <li className="flex gap-3"><span className="text-driftwood">-</span> Full payment is required at time of booking</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> All payments are processed securely through Stripe</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> Card details are never stored on our servers</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> Direct booking has no Airbnb guest fees or platform markup</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> Rates are quoted in US Dollars (USD)</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> Additional fees may apply for extended stays or special services</li>
                    </ul>
                  </div>

                  <div className="h-px bg-sand-deep w-full"></div>

                  <div>
                    <h3 className="text-luxury-lg font-serif text-forest mb-3">Security Deposit</h3>
                    <p className="text-ink-soft leading-relaxed">
                      {SECURITY_DEPOSIT_SUMMARY}
                    </p>
                  </div>

                  <div className="h-px bg-sand-deep w-full"></div>

                  <div>
                    <h3 className="text-luxury-lg font-serif text-forest mb-3">Liability</h3>
                    <p className="text-ink-soft leading-relaxed">
                      Guests acknowledge that they use the property at their own risk. We recommend 
                      purchasing travel insurance. The property owner and management company are not 
                      liable for accidents, injuries, or loss of personal belongings.
                    </p>
                  </div>
                </div>
              </div>

              {/* Privacy Policy */}
              <div id="privacy" className="bg-sand border border-sand-deep p-8 md:p-12">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-shell flex items-center justify-center border border-sand-deep">
                    <Shield className="h-6 w-6 text-ink" />
                  </div>
                  <h2 className="text-luxury-2xl font-serif text-forest tracking-luxury-tight">Privacy Policy</h2>
                </div>
                
                <div className="space-y-8">
                  <div>
                    <h3 className="text-luxury-lg font-serif text-forest mb-3">Information We Collect</h3>
                    <p className="text-ink-soft leading-relaxed mb-4">
                      We collect information necessary to process your booking and provide our services, including:
                    </p>
                    <ul className="space-y-2 text-ink-soft leading-relaxed">
                      <li className="flex gap-3"><span className="text-driftwood">-</span> Name, email address, and phone number</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> Payment information (processed securely by Stripe)</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> Communication preferences and special requests</li>
                      <li className="flex gap-3"><span className="text-driftwood">-</span> Usage data to improve our services</li>
                    </ul>
                  </div>

                  <div className="h-px bg-sand-deep w-full"></div>

                  <div>
                    <h3 className="text-luxury-lg font-serif text-forest mb-3">Data Security</h3>
                    <p className="text-ink-soft leading-relaxed">
                      We implement appropriate security measures to protect your personal information. 
                      Payment data is processed by Stripe and never stored on our servers. We do not 
                      sell, trade, or share your personal information with third parties except as 
                      necessary to provide our services.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        <section className="py-20 md:py-24 bg-shell border-y border-sand-deep">
          <div className="container mx-auto px-6 max-w-3xl">
            <FaqSection title="Policies FAQ" items={policyFaqs} className="px-0" />
          </div>
        </section>

        <section className="py-20 md:py-28 bg-sand border-t border-sand-deep">
          <div className="container mx-auto px-6 text-center max-w-2xl">
            <SectionHeading
              eyebrow="Contact"
              title="Questions About Our Policies?"
              lede="We're here to help clarify any questions you may have about our policies, terms, or booking process."
            />
            <ContactActionLinks
              phone={siteSettings?.contact_phone}
              email={siteSettings?.contact_email}
              variant="pills"
              className="mt-8 justify-center"
              testIdPrefix="policies"
            />

            <div className="mt-16 pt-10 border-t border-sand-deep text-xs text-driftwood uppercase tracking-[0.1em]">
              <p className="mb-2">Last updated: September 26, 2025</p>
              <p>{siteBrand.name} &middot; All policies subject to change with notice</p>
            </div>
          </div>
        </section>
      </main>

      <StickyMobileBookCta />
      <Footer />
    </div>
    </>
  );
}
